import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Droplets, Flame, Gauge, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { serviceStructure, getMainService } from '../data/services';

const PHONE = "403-615-8770";

const serviceIcons = {
  plumbing: Droplets,
  heating: Flame,
  'gas-fitting': Gauge,
};

const heroImages = {
  plumbing: '/images/dnb/plumbing-repair-1.jpg',
  heating: '/images/dnb/furnace-1.jpg',
  'gas-fitting': '/images/dnb/gas-line-1.jpg',
};

const heroAlts = {
  plumbing: 'Calgary residential plumbing services by DNB',
  heating: 'Calgary furnace and heating services by DNB',
  'gas-fitting': 'Licensed gas fitting and gas line work in Calgary by DNB',
};

const metaData = {
  plumbing: {
    title: 'Plumbing Services Calgary | Licensed Plumbers | DNB Plumbing, Heating & Gas Fitting Inc.',
    description: 'Professional plumbing services in Calgary by DNB. Water heaters, drain cleaning, Poly-B replacement, bathroom & kitchen plumbing. Call 403-615-8770.',
  },
  heating: {
    title: 'Heating Services Calgary | Furnace, Boiler & Hydronic Experts | DNB',
    description: 'Expert heating services in Calgary. Furnace repair & installation, boilers, in-floor radiant, garage heaters. Call DNB at 403-615-8770.',
  },
  'gas-fitting': {
    title: 'Gas Fitting Calgary | Licensed Gas Fitters | DNB Plumbing, Heating & Gas Fitting Inc.',
    description: 'Licensed gas fitting services in Calgary. BBQ gas lines, gas appliances, fireplaces, gas line installation, safety testing. Call 403-615-8770.',
  },
};

export const MainServicePage = () => {
  const { mainService } = useParams();
  
  const serviceData = getMainService(mainService);
  
  if (!serviceData) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#0F172A] hover:underline">Return to Homepage</Link>
        </div>
      </main>
    );
  }

  const Icon = serviceIcons[mainService] || Droplets;
  const heroImage = heroImages[mainService];
  const heroAlt = heroAlts[mainService] || serviceData.name;
  const meta = metaData[mainService];

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={`https://dnbplumbing.ca/${mainService}`} />
      </Helmet>

      <main data-testid="main-service-page" className="page-transition">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt={heroAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/85 to-[#0F172A]/70" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-white/70 text-sm mb-6">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <span className="text-white">{serviceData.name}</span>
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-white/10 rounded-xl">
                    <Icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                
                <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                  {serviceData.name} Services <span className="text-[#0EA5E9]">in Calgary</span>
                </h1>
                
                <p className="text-white/90 text-lg sm:text-xl mb-8 max-w-2xl">
                  {serviceData.description}. Expert technicians, fair pricing, and work done right the first time.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${PHONE}`}
                    data-testid="hero-call-btn"
                    className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call Now: {PHONE}
                  </a>
                  <Link
                    to="/contact"
                    data-testid="hero-book-btn"
                    className="bg-white hover:bg-slate-50 text-[#0F172A] font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Book Service
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-[#0F172A] uppercase tracking-widest text-sm font-semibold">Our Services</span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A] mt-3">
                {serviceData.name} Services We Offer
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceData.subServices.map((subService, index) => (
                <motion.div
                  key={subService.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/${mainService}/${subService.slug}`}
                    data-testid={`service-card-${subService.slug}`}
                    className="block h-full p-6 section-tint rounded-xl border border-slate-200 hover:border-[#0F172A]/30 hover:shadow-xl transition-all group"
                  >
                    <h3 className="font-heading font-bold text-xl text-[#0F172A] group-hover:text-[#0F172A] transition-colors mb-3">
                      {subService.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {subService.description}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {subService.services.slice(0, 3).map((service, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-[#0F172A]" />
                          {service}
                        </li>
                      ))}
                    </ul>
                    <span className="text-[#0F172A] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#111827] to-[#0F172A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
                Need {serviceData.name} Service in Calgary?
              </h2>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Contact DNB today. Fast response, fair pricing, quality work guaranteed.
              </p>
              
              <div className="mb-8">
                <a 
                  href={`tel:${PHONE}`}
                  className="text-white text-3xl sm:text-4xl font-black hover:text-[#0EA5E9] transition-colors"
                >
                  {PHONE}
                </a>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`tel:${PHONE}`}
                  className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
                <Link
                  to="/contact"
                  className="bg-white hover:bg-slate-50 text-[#0F172A] font-bold uppercase tracking-wider px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                >
                  Book Service
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MainServicePage;
